import { LitElement, css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/card/card.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/badge/badge.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/divider/divider.js';
import '@shoelace-style/shoelace/dist/components/tab-group/tab-group.js';
import '@shoelace-style/shoelace/dist/components/tab/tab.js';
import '@shoelace-style/shoelace/dist/components/tab-panel/tab-panel.js';
import { WithUserProfileConsumer } from '../mixins/with-user-profile-consumer';
import { WithActiveShopConsumer } from '../mixins/with-active-shop-consumer';
import { ApiFactory } from '../api/easypos-api';
import { ILowStockReport, ISalesReport } from '../api/model/requests';
import { styles as sharedStyles } from '../styles/shared-styles';

const Base = WithActiveShopConsumer(WithUserProfileConsumer(LitElement));

// Owner reports dashboard. Two tabs:
//   Sales — gross / cost / profit, payment split, top items
//   Low stock — items at or below their reorder level
// Variance lives on the stock-take page itself; cross-linked from there.
// Loss tracking is variance-from-stock-take; there is no separate waste path.
@customElement('sales-reports-page')
export class SalesReportsPage extends Base {
  @state() private _tab: 'sales' | 'low' = 'sales';
  @state() private _from = isoDaysAgo(29);
  @state() private _to = isoToday();
  @state() private _sales: ISalesReport | null = null;
  @state() private _low: ILowStockReport | null = null;
  @state() private _loading = true;
  @state() private _error: string | null = null;

  static styles = [
    sharedStyles,
    css`
      :host {
        display: block;
        max-width: 900px;
        margin: 0 auto;
        padding: 16px;
        box-sizing: border-box;
      }
      .filter-row {
        display: flex;
        gap: 8px;
        align-items: end;
        flex-wrap: wrap;
        margin-bottom: 12px;
      }
      .filter-row sl-input {
        max-width: 180px;
      }
      .kpis {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
        gap: 8px;
        margin-bottom: 12px;
      }
      .kpi {
        background: var(--sl-color-neutral-50);
        padding: 12px;
        border-radius: var(--sl-border-radius-medium);
      }
      .kpi-label {
        font-size: 0.8rem;
        opacity: 0.7;
      }
      .kpi-value {
        font-size: 1.5rem;
        font-weight: 700;
        font-variant-numeric: tabular-nums;
      }
      table {
        width: 100%;
        border-collapse: collapse;
      }
      th,
      td {
        text-align: left;
        padding: 6px 4px;
        border-bottom: 1px solid var(--sl-color-neutral-200);
      }
      th.num,
      td.num {
        text-align: right;
        font-variant-numeric: tabular-nums;
      }
      .pos {
        color: var(--sl-color-success-700);
      }
      .neg {
        color: var(--sl-color-danger-700);
      }
    `,
  ];

  firstUpdated() {
    void this._refresh();
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has('activeShop') && this.activeShop) void this._refresh();
  }

  private async _refresh() {
    if (!this.activeShop) {
      this._error = 'No active shop.';
      this._loading = false;
      return;
    }
    this._loading = true;
    this._error = null;
    const api = ApiFactory.authenticated();
    if (this._tab === 'sales') {
      const r = await api.salesReport(
        this.activeShop.shopName,
        this._from.replaceAll('-', ''),
        this._to.replaceAll('-', '')
      );
      if (!r.success || !r.data) this._error = r.error ?? 'Failed.';
      else this._sales = r.data;
    } else {
      const r = await api.lowStockReport(this.activeShop.shopName);
      if (!r.success || !r.data) this._error = r.error ?? 'Failed.';
      else this._low = r.data;
    }
    this._loading = false;
  }

  private _setTab(t: 'sales' | 'low') {
    this._tab = t;
    void this._refresh();
  }

  private _renderSales() {
    return html`
      <div class="filter-row">
        <sl-input
          label="From"
          type="date"
          .value=${this._from}
          @sl-change=${(e: Event) => {
            this._from = (e.target as HTMLInputElement).value;
          }}
        ></sl-input>
        <sl-input
          label="To"
          type="date"
          .value=${this._to}
          @sl-change=${(e: Event) => {
            this._to = (e.target as HTMLInputElement).value;
          }}
        ></sl-input>
        <sl-button variant="primary" @click=${this._refresh}>Apply</sl-button>
      </div>

      ${
        this._sales
          ? html`
              <div class="kpis">
                ${kpi('Gross sales', currency(this._sales.grossSales))}
                ${kpi('Cost of goods', currency(this._sales.costOfGoods))}
                ${kpi('Gross profit', currency(this._sales.grossProfit))}
                ${kpi('Sales', String(this._sales.saleCount))}
                ${kpi('Cancelled', String(this._sales.cancelledCount))}
              </div>

              <sl-card>
                <strong>Payment split</strong>
                <table>
                  <thead>
                    <tr>
                      <th>Method</th>
                      <th class="num">Sales</th>
                      <th class="num">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${
                      this._sales.paymentSplit.length === 0
                        ? html`<tr>
                            <td colspan="3">
                              <em>No sales in this window.</em>
                            </td>
                          </tr>`
                        : this._sales.paymentSplit.map(
                            (p) => html`
                              <tr>
                                <td>${p.method}</td>
                                <td class="num">${p.saleCount}</td>
                                <td class="num">${currency(p.total)}</td>
                              </tr>
                            `
                          )
                    }
                  </tbody>
                </table>
              </sl-card>

              <sl-card style="margin-top: 12px;">
                <strong>Top items</strong>
                <table>
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th class="num">Qty</th>
                      <th class="num">Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${
                      this._sales.topItems.length === 0
                        ? html`<tr>
                            <td colspan="3"><em>—</em></td>
                          </tr>`
                        : this._sales.topItems.map(
                            (l) => html`
                              <tr>
                                <td>${l.name}</td>
                                <td class="num">${l.quantity}</td>
                                <td class="num">${currency(l.revenue)}</td>
                              </tr>
                            `
                          )
                    }
                  </tbody>
                </table>
              </sl-card>
            `
          : nothing
      }
    `;
  }

  private _renderLow() {
    return html`
      ${
        this._low
          ? html`
              <div class="kpis">
                ${kpi('Items below reorder', String(this._low.threshold))}
              </div>
              <sl-card>
                <table>
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th class="num">On hand</th>
                      <th class="num">Reorder at</th>
                      <th class="num">Shortfall</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${
                      this._low.items.length === 0
                        ? html`<tr>
                            <td colspan="4">
                              <em>Everything's stocked. Nice.</em>
                            </td>
                          </tr>`
                        : this._low.items.map(
                            (l) => html`
                              <tr>
                                <td>
                                  ${l.name} <small>(${l.baseUnit})</small>
                                </td>
                                <td class="num">${l.onHand}</td>
                                <td class="num">${l.reorderLevel}</td>
                                <td class="num neg">${l.shortfall}</td>
                              </tr>
                            `
                          )
                    }
                  </tbody>
                </table>
              </sl-card>
            `
          : nothing
      }
    `;
  }

  render() {
    return html`
      <h2>Reports</h2>
      ${this._error ? html`<sl-alert variant="danger" open>${this._error}</sl-alert>` : nothing}

      <sl-tab-group
        @sl-tab-show=${(e: Event) => {
          const name = (e as CustomEvent<{ name: string }>).detail.name;
          this._setTab(name as 'sales' | 'low');
        }}
      >
        <sl-tab slot="nav" panel="sales" ?active=${this._tab === 'sales'}
          >Sales</sl-tab
        >
        <sl-tab slot="nav" panel="low" ?active=${this._tab === 'low'}
          >Low stock</sl-tab
        >
        <sl-tab-panel name="sales"
          >${this._loading ? html`<p>Loading…</p>` : this._renderSales()}</sl-tab-panel
        >
        <sl-tab-panel name="low"
          >${this._loading ? html`<p>Loading…</p>` : this._renderLow()}</sl-tab-panel
        >
      </sl-tab-group>
    `;
  }
}

function kpi(label: string, value: string) {
  return html`
    <div class="kpi">
      <div class="kpi-label">${label}</div>
      <div class="kpi-value">${value}</div>
    </div>
  `;
}

function currency(n: number): string {
  return n.toLocaleString(undefined, {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function isoToday(): string {
  return new Date().toISOString().slice(0, 10);
}

function isoDaysAgo(d: number): string {
  const now = new Date();
  now.setUTCDate(now.getUTCDate() - d);
  return now.toISOString().slice(0, 10);
}
